import { faBullhorn, faPen, faStar, faUsers } from "@fortawesome/free-solid-svg-icons";

export const influencersRoleDescription = {
  roleId: 'influencers',
  title: 'Influencers',
  sections: [
    {
      id: 'overview',
      title: 'Overview',
      displayTitle: 'Influencers',
      subtitle: 'Create. Share. Influence.',
      description: 'Influencers are key drivers of growth and engagement for Spitface Battles beta. Publish content, share feedback, and activate your audience to help grow the platform. Your influence extends beyond your own profile — you will drive discovery, grow the community, and provide actionable insights that impact the platform.',
      contentItems: [
        '**Beta Window:** 12 months or until V1 launch',
        '**Signup Capacity:** 300'
      ],
      highlights: [
        "200 Official Spots",
        "Content Creation",
        "Impact & Feedback"
      ],
      moreItems: [
        {
          key: "publishing",
          label: "Publishing",
          icon: faPen
        },
        {
          key: "promotion",
          label: "Promotion",
          icon: faBullhorn
        },
        {
          key: "engagement",
          label: "Engagement",
          icon: faUsers
        },
        {
          key: "feedback",
          label: "Feedback",
          icon: faStar
        }
      ]
    },
    {
      id: 'whatYoullDo',
      title: "What You'll Do",
      description: 'Publish. Promote. Engage. Keep your creator lane consistent.',
      lists: [{
        title: 'Core Duties',
        items: [
          'Publish battle-related posts, videos, or streams on a recurring schedule',
          'Promote platform participation through regular shares and audience calls to action',
          'Stay active in Discord channels and community discussions',
          'Submit actionable monthly usability and discoverability feedback'
        ]
      }, {
        title: 'Collaboration & Communication',
        items: [
          'Work with SFB on creator campaigns and content initiatives',
          'Support other beta roles through visible participation',
          'Maintain creator standards across the full beta cycle'
        ]
      }],
      contentItems: [],
      tables: []
    },
    {
      id: 'whyThisRoleMatters',
      title: 'Why This Role Matters',
      description: 'Creators shape how new users discover and engage with SFB. Strong consistency drives quality growth, participation, and feature direction.',
      contentItems: ['**Note:** This role uses manual verification for quality and consistency.'],
      lists: [{
        title: 'This role directly helps:',
        items: [
          'Expand reach through creator-led discovery',
          'Convert audience attention into participation',
          'Improve creator and discovery features via practical feedback'
        ]
      }],
      tables: []
    },
    {
      id: 'whatYouGet',
      title: 'What You Get',
      description: 'Recognition, visibility, and influence for consistent creators.',
      lists: [
        {
          title: 'Provisional Status',
          items: [
            'Earned by meeting first 3-month content/promotion gate',
            'Progressive premium unlocks through beta phases',
            'Provisional recognition and creator visibility priority'
          ]
        },
        {
          title: 'Official Status',
          items: [
            'Top 200 most consistent creators earn official status',
            'Lifetime role premium, permanent Hall of Fame recognition, creator influence privileges'
          ]
        },
        {
          title: 'Runner-Up Status',
          items: [
            'Retain premium value and recognition for growth impact if outside official cap'
          ]
        }
      ],
      contentItems: [],
      tables: []
    },
    {
      id: 'expectations',
      title: 'Expectations',
      description: 'Phase-based targets and weekly rhythm to maintain consistent output and community engagement.',
      lists: [{
        title: 'Performance Standards',
        items: [
          'Completion threshold target: 90%',
          'Ranked by reputation and consistency',
          'Final status awarded by tracked contribution quality over time'
        ]
      }, {
        title: 'Typical Weekly Rhythm',
        items: [
          'Publish content consistently around battles/features',
          'Maintain promotional cadence and audience engagement',
          'Participate in Discord and support role/community conversations',
          'Submit actionable product feedback monthly'
        ]
      }, {
        title: 'Consistency Markers',
        items: [
          'Stable publishing cadence',
          'Sustained promotional activity',
          'Reliable collaboration and quality feedback'
        ]
      }],
      contentItems: ['**Important:** Thresholds may be adjusted before beta launch.'],
      tables: [{
        title: 'Requirements By Phase',
        headers: [
          '',
          'phase 1\nclosed beta',
          'phase 2\nopen beta',
          'phase 3\nopen beta',
        ],
        rows: [{
          requirement: 'publishing',
          metric: 'posts/videos',
          timeframe: 'monthly',
          phase1: '8',
          phase2: '4',
          phase3: '4',
          execution: '[NEED EXECUTION TEXT. IS EITHER IN BETA ROLES OF ROLE DESCRIPTION OR NEED NEW LIKE FOUNDING 50 & DAY ONES]'
        }, {
          requirement: 'promotion',
          metric: 'shares',
          timeframe: 'monthly',
          phase1: '12',
          phase2: '8',
          phase3: '8',
          execution: '[NEED EXECUTION TEXT. IS EITHER IN BETA ROLES OF ROLE DESCRIPTION OR NEED NEW LIKE FOUNDING 50 & DAY ONES]'
        }, {
          requirement: 'community',
          metric: 'discord',
          timeframe: 'monthly',
          phase1: '8',
          phase2: '8',
          phase3: '8',
          execution: '[NEED EXECUTION TEXT. IS EITHER IN BETA ROLES OF ROLE DESCRIPTION OR NEED NEW LIKE FOUNDING 50 & DAY ONES]'
        }, {
          requirement: 'feedback',
          metric: 'submissions',
          timeframe: 'monthly',
          phase1: '1',
          phase2: '1',
          phase3: '1',
          execution: '[NEED EXECUTION TEXT. IS EITHER IN BETA ROLES OF ROLE DESCRIPTION OR NEED NEW LIKE FOUNDING 50 & DAY ONES]'
        }]
      }]
    },
    {
      id: 'whoShouldApply',
      title: 'Who Should Apply',
      description: 'Determine if this creator lane fits your style, capacity, and goals.',
      lists: [{
        title: 'Ideal For:',
        items: [
          'Have an audience and consistent content routine',
          'Can convert visibility into platform participation',
          'Want influence on creator/discovery tooling',
          'Can sustain quality contribution over the full beta'
        ]
      }, {
        title: 'Not Ideal For:',
        items: [
          'Cannot maintain recurring content cadence',
          'Prefer untracked or low-accountability participation'
        ]
      }],
      contentItems: [],
      tables: []
    },
    {
      id: 'acknowledgements',
      title: 'Acknowledgements',
      description: 'Confirm understanding of expectations, commitment, and community standards before joining.',
      lists: [{
        title: 'Commitment Checks',
        items: [
          'Commit to full-cycle creator participation',
          'Accept manual verification of output and impact',
          'Participate in Discord during beta',
          'Complete required signup acknowledgements'
        ]
      }],
      contentItems: [],
      tables: []
    },
    {
      id: 'join',
      title: 'Join',
      description: 'Ready to step in? Confirm acknowledgements and complete your signup.',
      lists: [{
        title: 'Next Steps',
        items: [
          'Click Apply',
          'Check acknowledgements',
          'Confirm commitment in modal'
        ]
      }],
      contentItems: [],
      tables: []
    }
  ]
};